import React, { useEffect, useMemo, useState } from 'react';
import { Heart, Volume2, VolumeX, Activity, Edit2, Check, TrendingUp, TrendingDown, Info, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../store/authStore';
import { getSessions, getReadings } from '../../api/sessions';
import { useDeviceWebSocket } from '../../hooks/useDeviceWebSocket';
import { useHeartbeatSound } from '../../hooks/useHeartbeatSound';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { BabyCameraCard } from './BabyCameraCard';
const FRUIT_MAP: Record<number, string> = {
  20: 'a banana 🍌',
  24: 'a corn cob 🌽',
  28: 'an eggplant 🍆',
  32: 'a squash 🥒',
  36: 'a honeydew melon 🍈',
  40: 'a watermelon 🍉'
};
function getFruitForWeek(week: number) {
  const weeks = Object.keys(FRUIT_MAP).map(Number).sort((a, b) => b - a);
  for (const w of weeks) {
    if (week >= w) return FRUIT_MAP[w];
  }
  return 'a sweet potato 🍠';
}
export function BabyScreen() {
  const {
    token
  } = useAuth();
  const {
    isConnected,
    bpm: liveBpm,
    piezoValue,
    cameraFrame,
    cameraStatus
  } = useDeviceWebSocket(token);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionData, setSessionData] = useState<any>(null);
  const [bpmReadings, setBpmReadings] = useState<any[]>([]);
  const [piezoReadings, setPiezoReadings] = useState<any[]>([]);
  // Local Storage States
  const [babyName, setBabyName] = useState(() => {
    return localStorage.getItem('aurora_baby_name') || 'Little One';
  });
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(babyName);
  const [kickLog, setKickLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_kick_log');
    return saved ? JSON.parse(saved) : [];
  });
  const [babyPosition, setBabyPosition] = useState(() => {
    return localStorage.getItem('aurora_baby_position') || 'Head Down';
  });
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const sessionsRes = await getSessions(token);
        if (sessionsRes && sessionsRes.length > 0) {
          const latestSession = sessionsRes[0];
          setSessionData(latestSession);
          if (latestSession.id) {
            const [bpmRes, piezoRes] = await Promise.all([getReadings(token, latestSession.id, {
              sensorType: 'fhr',
              limit: 60
            }), getReadings(token, latestSession.id, {
              sensorType: 'piezo',
              limit: 100
            })]);
            if (bpmRes && bpmRes.data) setBpmReadings(bpmRes.data.reverse());
            if (piezoRes && piezoRes.data) setPiezoReadings(piezoRes.data.reverse());
          }
        }
      } catch (err) {
        // Handle silently
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token]);
  // Derived Data
  const pregnancyWeek = 24; // Hardcoded for demo
  const fruitComparison = getFruitForWeek(pregnancyWeek);
  const displayBpm = isConnected && liveBpm ? liveBpm : sessionData?.avgBpm || null;
  const {
    soundEnabled,
    toggleSound
  } = useHeartbeatSound(displayBpm, isConnected || !!sessionData);
  const bpmColor = useMemo(() => {
    if (!displayBpm) return '#8A8A8A';
    if (displayBpm < 100 || displayBpm > 170) return '#E8735A'; // Red
    if (displayBpm < 110 || displayBpm > 160) return '#F59E0B'; // Amber
    return '#00D4D4'; // Teal
  }, [displayBpm]);
  const bpmTrendData = useMemo(() => {
    if (!bpmReadings.length) return [];
    return bpmReadings.map((r: any) => ({
      time: new Date(r.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      bpm: r.value
    }));
  }, [bpmReadings]);
  const bpmChange = useMemo(() => {
    if (bpmReadings.length < 2) return null;
    const first = bpmReadings[0].value;
    const last = bpmReadings[bpmReadings.length - 1].value;
    const diff = last - first;
    const pct = (diff / first * 100).toFixed(1);
    return {
      diff,
      pct,
      up: diff >= 0
    };
  }, [bpmReadings]);
  const isBabyActive = isConnected && piezoValue > 2000;
  const todayKicks = useMemo(() => {
    const todayStr = new Date().toDateString();
    return kickLog.filter(k => new Date(k.timestamp).toDateString() === todayStr);
  }, [kickLog]);
  const lastKickTime = useMemo(() => {
    if (!todayKicks.length) return null;
    const last = todayKicks[0];
    const diffMins = Math.floor((Date.now() - new Date(last.timestamp).getTime()) / 60000);
    return diffMins;
  }, [todayKicks]);
  const kickChartData = useMemo(() => {
    const data = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toDateString();
      const count = kickLog.filter(k => new Date(k.timestamp).toDateString() === dateStr).length;
      data.push({
        day: d.toLocaleDateString([], {
          weekday: 'short'
        }),
        kicks: count
      });
    }
    return data;
  }, [kickLog]);
  const sleepWakeData = useMemo(() => {
    if (!piezoReadings.length) return [];
    // Simplify piezo data into active/resting blocks
    return piezoReadings.map((r: any) => ({
      time: new Date(r.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      status: r.value > 1500 ? 1 : 0
    }));
  }, [piezoReadings]);
  const dailyStatus = useMemo(() => {
    if (todayKicks.length > 15) return 'Very Active';
    if (todayKicks.length > 5) return 'Normal Activity';
    return 'Resting Peacefully';
  }, [todayKicks]);
  // Handlers
  const handleSaveName = () => {
    if (nameInput.trim()) {
      setBabyName(nameInput.trim());
      localStorage.setItem('aurora_baby_name', nameInput.trim());
    }
    setIsEditingName(false);
  };
  const handleLogKick = () => {
    const newLog = [{
      timestamp: new Date().toISOString()
    }, ...kickLog];
    setKickLog(newLog);
    localStorage.setItem('aurora_kick_log', JSON.stringify(newLog));
  };
  const handlePositionChange = (pos: string) => {
    setBabyPosition(pos);
    localStorage.setItem('aurora_baby_position', pos);
  };
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0 animate-fadeInUp space-y-6" data-id="element-772">
      {/* 0. LIVE BABY CAMERA */}
      <BabyCameraCard token={token} cameraFrame={cameraFrame} cameraStatus={cameraStatus} data-id="element-773" />

      {/* 1. BABY HEADER */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 relative overflow-hidden" data-id="element-774">
        <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-bl-full -z-0" data-id="element-775" />
        <div className="relative z-10" data-id="element-776">
          <div className="flex items-center mb-2" data-id="element-777">
            {isEditingName ? <div className="flex items-center" data-id="element-778">
                <input type="text" value={nameInput} onChange={e => setNameInput(e.target.value)} className="text-[24px] font-semibold text-text-primary bg-secondary border border-border rounded-lg px-3 py-1 outline-none focus:border-primary w-[200px]" autoFocus data-id="element-779" />
                <button onClick={handleSaveName} className="ml-2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center" data-id="element-780">
                  <Check size={16} data-id="element-781" />
                </button>
              </div> : <div className="flex items-center" data-id="element-782">
                <h1 className="text-[24px] font-semibold text-text-primary mr-3" data-id="element-783">
                  {babyName}
                </h1>
                <button onClick={() => setIsEditingName(true)} className="text-text-secondary hover:text-primary transition-colors" data-id="element-784">
                  <Edit2 size={16} data-id="element-785" />
                </button>
              </div>}
          </div>
          <p className="text-[15px] text-text-secondary mb-4" data-id="element-786">
            Week {pregnancyWeek} · Estimated
          </p>
          <div className="bg-secondary/50 rounded-[16px] p-4 inline-block" data-id="element-787">
            <p className="text-[14px] font-medium text-text-primary" data-id="element-788">
              Currently the size of {fruitComparison}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-789">
        {/* 2. LIVE HEART RATE */}
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 flex flex-col items-center justify-center text-center relative" data-id="element-790">
          <button onClick={toggleSound} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-secondary text-primary flex items-center justify-center hover:bg-primary/10 transition-colors" aria-label={soundEnabled ? 'Mute heartbeat' : 'Play heartbeat'} data-id="element-791">
            {soundEnabled ? <Volume2 size={20} data-id="element-792" /> : <VolumeX size={20} data-id="element-793" />}
          </button>

          <h2 className="text-[16px] font-semibold text-text-primary mb-6 w-full text-left" data-id="element-794">
            Heart Rate
          </h2>

          <motion.div animate={{
          scale: displayBpm ? [1, 1.15, 1] : 1
        }} transition={{
          duration: displayBpm ? 60 / displayBpm : 1,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="mb-6" data-id="element-795">
            <Heart size={80} color={bpmColor} fill={bpmColor} className="drop-shadow-lg" data-id="element-796" />
          </motion.div>

          <div className="flex flex-col items-center" data-id="element-797">
            <span className="text-[48px] font-bold leading-none" style={{
            color: bpmColor
          }} data-id="element-798">
              {displayBpm || '--'}
            </span>
            <span className="text-[14px] text-text-secondary mt-1 font-medium tracking-wider uppercase" data-id="element-799">
              BPM
            </span>
          </div>
        </div>

        {/* 3. BPM TREND & 4. REAL-TIME MOVEMENT */}
        <div className="flex flex-col gap-6" data-id="element-800">
          <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 flex-1" data-id="element-801">
            <div className="flex justify-between items-start mb-4" data-id="element-802">
              <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-803">
                BPM Trend
              </h2>
              {bpmChange && <div className={`flex items-center text-[13px] font-medium ${bpmChange.up ? 'text-success' : 'text-danger'}`} data-id="element-804">
                  {bpmChange.up ? <TrendingUp size={16} className="mr-1" data-id="element-805" /> : <TrendingDown size={16} className="mr-1" data-id="element-806" />}
                  {Math.abs(bpmChange.diff)} ({bpmChange.pct}%)
                </div>}
            </div>

            {isLoading ? <SkeletonBlock height="80px" data-id="element-807" /> : bpmTrendData.length > 0 ? <div className="h-[80px] w-full" data-id="element-808">
                <ResponsiveContainer width="100%" height="100%" data-id="element-809">
                  <LineChart data={bpmTrendData} data-id="element-810">
                    <Line type="monotone" dataKey="bpm" stroke="#00D4D4" strokeWidth={3} dot={false} data-id="element-811" />
                  </LineChart>
                </ResponsiveContainer>
              </div> : <div className="h-[80px] flex items-center justify-center text-[13px] text-text-secondary" data-id="element-812">
                No recent session data
              </div>}
          </div>

          <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 flex items-center justify-between" data-id="element-813">
            <div data-id="element-814">
              <h2 className="text-[16px] font-semibold text-text-primary mb-1" data-id="element-815">
                Movement
              </h2>
              <p className="text-[14px] text-text-secondary" data-id="element-816">
                {isBabyActive ? 'Baby is Active' : 'Baby is Resting'}
              </p>
            </div>
            <div className="relative flex h-6 w-6" data-id="element-817">
              {isBabyActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" data-id="element-818"></span>}
              <span className={`relative inline-flex rounded-full h-6 w-6 ${isBabyActive ? 'bg-success' : 'bg-border'}`} data-id="element-819"></span>
            </div>
          </div>
        </div>
      </div>

      {/* 5. KICK COUNTER */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-820">
        <div className="flex justify-between items-center mb-6" data-id="element-821">
          <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-822">
            Kick Counter
          </h2>
          <div className="bg-primary/10 text-primary px-3 py-1 rounded-pill text-[14px] font-semibold" data-id="element-823">
            {todayKicks.length} today
          </div>
        </div>

        {todayKicks.length >= 10 && <div className="bg-success/10 border border-success/20 rounded-[16px] p-4 mb-6 flex items-center text-success" data-id="element-824">
            <span className="text-[20px] mr-3" data-id="element-825">🎉</span>
            <span className="text-[14px] font-medium" data-id="element-826">
              10 kicks reached! Baby is doing great.
            </span>
          </div>}

        <button onClick={handleLogKick} className="w-full h-[80px] bg-primary text-white rounded-[20px] text-[18px] font-semibold shadow-md hover:bg-primary/90 transition-colors mb-6 flex items-center justify-center active:scale-[0.98]" data-id="element-827">
          <span className="text-[24px] mr-3" data-id="element-828">👶</span>
          Tap when baby kicks
        </button>

        <p className="text-[13px] text-text-secondary text-center mb-8" data-id="element-829">
          {lastKickTime !== null ? `Last kick: ${lastKickTime}m ago` : 'No kicks logged today'}
        </p>

        <div className="h-[120px] w-full" data-id="element-830">
          <ResponsiveContainer width="100%" height="100%" data-id="element-831">
            <BarChart data={kickChartData} data-id="element-832">
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
              fontSize: 12,
              fill: '#8A8A8A'
            }} data-id="element-833" />
              <Tooltip cursor={{
              fill: '#F3E8FF'
            }} contentStyle={{
              borderRadius: '12px',
              border: 'none'
            }} data-id="element-834" />
              <Bar dataKey="kicks" fill="#6B21E8" radius={[4, 4, 0, 0]} data-id="element-835" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 6. SLEEP/WAKE CYCLE */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-836">
        <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-837">
          Sleep/Wake Cycle
        </h2>
        <p className="text-[13px] text-text-secondary mb-6 flex items-center" data-id="element-838">
          <Info size={14} className="mr-1.5" data-id="element-839" />
          Estimated based on movement patterns
        </p>

        {isLoading ? <SkeletonBlock height="60px" data-id="element-840" /> : sleepWakeData.length > 0 ? <div className="h-[60px] w-full" data-id="element-841">
            <ResponsiveContainer width="100%" height="100%" data-id="element-842">
              <BarChart data={sleepWakeData} barGap={0} barCategoryGap={0} data-id="element-843">
                <Tooltip contentStyle={{
              borderRadius: '12px',
              border: 'none'
            }} data-id="element-844" />
                <Bar dataKey="status" fill="#00D4D4" radius={[2, 2, 0, 0]} data-id="element-845" />
              </BarChart>
            </ResponsiveContainer>
          </div> : <div className="h-[60px] bg-secondary rounded-[12px] flex items-center justify-center text-[13px] text-text-secondary" data-id="element-846">
            No recent session data to estimate cycle
          </div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-847">
        {/* 7. BABY POSITION */}
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-848">
          <h2 className="text-[18px] font-semibold text-text-primary mb-4" data-id="element-849">
            Baby Position
          </h2>

          <div className="flex flex-col gap-3 mb-6" data-id="element-850">
            {['Head Down', 'Breech', 'Transverse'].map(pos => <button key={pos} onClick={() => handlePositionChange(pos)} className={`w-full py-3 px-4 rounded-[14px] text-[14px] font-medium text-left transition-colors border ${babyPosition === pos ? 'bg-primary/10 border-primary text-primary' : 'bg-white border-border text-text-secondary hover:bg-secondary'}`} data-id="element-851">
                {pos}
              </button>)}
          </div>

          {pregnancyWeek >= 36 && babyPosition !== 'Head Down' && <div className="bg-amber-500/10 border border-amber-500/20 rounded-[12px] p-3 flex items-start" data-id="element-852">
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-853" />
              <p className="text-[13px] text-amber-700 leading-tight" data-id="element-854">
                Consider discussing positioning with your doctor.
              </p>
            </div>}
        </div>

        {/* 8. DAILY STATUS SUMMARY */}
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 flex flex-col items-center justify-center text-center" data-id="element-855">
          <h2 className="text-[16px] font-semibold text-text-secondary uppercase tracking-wider mb-6" data-id="element-856">
            Daily Status
          </h2>
          <div className="w-24 h-24 rounded-full bg-accent/10 flex items-center justify-center mb-4" data-id="element-857">
            <Activity size={40} className="text-accent" data-id="element-858" />
          </div>
          <h3 className="text-[22px] font-bold text-text-primary mb-2" data-id="element-859">
            {dailyStatus}
          </h3>
          <p className="text-[14px] text-text-secondary max-w-[200px]" data-id="element-860">
            Based on {todayKicks.length} kicks and recent movement data.
          </p>
        </div>
      </div>
    </main>;
}